import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Tool } from "@/types";
import { toast } from "sonner";
import { useEffect } from "react";

interface UseToolsProps {
  currentPage: number;
  selectedCategory: string;
  searchQuery: string;
  itemsPerPage: number;
}

export const useTools = ({ currentPage, selectedCategory, searchQuery, itemsPerPage }: UseToolsProps) => {
  const queryClient = useQueryClient();

  const { data, refetch, ...rest } = useQuery({
    queryKey: ['tools', currentPage, selectedCategory, searchQuery],
    queryFn: async () => {
      let query = supabase
        .from('tools')
        .select('*, tool_interactions(likes)', { count: 'exact' });

      if (selectedCategory !== "الكل") {
        query = query.contains('categories', [selectedCategory]);
      }

      if (searchQuery) {
        query = query.or(`name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
      }

      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;
      
      const { data, error, count } = await query
        .order('created_at', { ascending: false })
        .range(from, to);
      
      if (error) {
        toast.error('حدث خطأ أثناء تحميل الأدوات');
        throw error;
      }
      
      return {
        tools: data.map(tool => ({
          id: tool.id,
          name: tool.name,
          description: tool.description,
          imageUrl: tool.image_url,
          categories: tool.categories,
          is_featured: tool.is_featured || false,
          likes: tool.tool_interactions?.reduce((sum: number, curr: any) => sum + (curr.likes || 0), 0) || 0,
          created_at: tool.created_at,
          updated_at: tool.updated_at,
          usage_guide: tool.usage_guide || '',
          tutorial_video_url: tool.tutorial_video_url || '',
          examples: tool.examples ? Array.isArray(tool.examples) ? tool.examples : [] : []
        })) as Tool[],
        totalCount: count || 0
      };
    },
    staleTime: 1000, // Set to 1 second to make data stale quickly
    refetchOnWindowFocus: false, // Disable refetch on window focus
    refetchOnMount: true, // Always refetch on mount
  });

  useEffect(() => {
    // Subscribe to real-time changes
    const channel = supabase
      .channel('tools-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tool_interactions'
        },
        () => {
          // Invalidate and refetch when tool_interactions change
          queryClient.invalidateQueries({ queryKey: ['tools'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return { data, refetch, ...rest };
};